import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import {Box, Button, Container, Divider, Stack, Typography, useMediaQuery} from '@mui/material';
import {useLocation, useNavigate} from 'react-router-dom';
import {postAuthCodeSend} from '../../../api/auth';
import {AuthHeading, AuthLogo} from '../base';

/**
 * Custom button component with adjusted styling.
 *
 * @param {Object} props - The props object.
 * @param {Object} props.sx - Additional styles for the button.
 * @param {React.ReactNode} props.children - The content of the button.
 * @param {Object} props.otherProps - Additional props for the button.
 * @returns {JSX.Element} - The rendered custom button component.
 */
const CustomButton = ({sx, children, ...otherProps}) => {
  return (
    <Button
      sx={{
        justifyContent: 'start',
        fontSize: 16,
        textAlign: 'start',
        ...sx,
        '.MuiButton-startIcon svg': {
          width: 23,
          height: 23,
        },
      }}
      {...otherProps}
    >
      {children}
    </Button>
  );
};

/**
 * Component for the Two-Factor Authentication Page.
 *
 * @returns {JSX.Element} - The rendered Two-Factor Authentication Page component.
 */
export const TwoFactorAuthRealPage = () => {
  const isSmallScreen = useMediaQuery(theme => theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const {state} = useLocation();
  const {email = undefined, password = undefined, maskedEmail = undefined, maskedPhone = undefined} = state || {};

  return (
    <Container maxWidth="sm">
      <Stack spacing={2} alignItems="start" justifyContent="center" sx={{height: '100vh', px: {xs: 3, sm: undefined}}}>
        <AuthLogo />
        <AuthHeading fontSize={isSmallScreen ? '6vw' : 'normal'}>2 Factor Authentication</AuthHeading>
        <Typography color="darkgray" sx={{fontSize: {xs: 18, sm: 20}, textAlign: 'justify'}}>
          To ensure the security of your account, AI-MD implements an additional verification process to confirm your
          identity during sign-in attempts.
        </Typography>
        <Typography sx={{fontSize: 20, fontWeight: 600}}>Select a sign-in method:</Typography>
        <Box
          size="large"
          sx={{
            display: 'flex',
            width: '100%',
            flexDirection: 'column',
            gap: 1,
          }}
        >
          <CustomButton
            key="email"
            startIcon={<EmailOutlinedIcon />}
            onClick={() => {
              postAuthCodeSend(email, password, 'email');
              navigate('/authenticate-email', {
                state: {email, password, maskedEmail, maskedPhone},
              });
            }}
          >
            Get a verification code at{isSmallScreen ? <br /> : ''} {maskedEmail}
          </CustomButton>
          <Divider />
          <CustomButton
            key="phone"
            startIcon={<PhoneIphoneIcon />}
            onClick={() => {
              postAuthCodeSend(email, password, 'phone');
              navigate('/authenticate-phone', {
                state: {email, password, maskedEmail, maskedPhone},
              });
            }}
            sx={{
              display: maskedPhone === '' || maskedPhone === undefined ? 'none' : undefined,
            }}
          >
            Get a verification code at{isSmallScreen ? <br /> : ''} {maskedPhone}
          </CustomButton>
        </Box>
      </Stack>
    </Container>
  );
};
